/**
 * =============================================================================
 * ROASTBREW - SHARED CONFIGURATION
 * =============================================================================
 * 
 * This file contains all shared constants used across the application.
 * Centralizing these values ensures consistency and makes updates easier.
 * 
 * CONTENTS:
 * - TIER_COLORS: Theme colors for each subscription tier (free, pro, pro_plus)
 * - TIERS: Subscription tier definitions with features and pricing
 * - Helper functions for pricing and color retrieval
 * 
 * USAGE:
 * import { TIER_COLORS, TIERS } from './config';
 * 
 * =============================================================================
 */

// =============================================================================
// TIER_COLORS - Theme configuration for each subscription level
// =============================================================================

export const TIER_COLORS = {
  free: { 
    bg: '#F5F5F0',
    bgAlt: '#EEEEE8',
    sidebar: '#EAEAE5',
    accent: '#8B8B7A',
    accentGlow: 'rgba(139,139,122,0.3)',
    text: '#1A1A1A',
    textSecondary: '#4A4A42',
    muted: '#6B6B5F',
    cardBg: '#FFFFFF',
    overlay: 'rgba(245,245,240,0.95)',
    label: 'Free',
  },
  pro: { 
    bg: '#1A1A1A',
    bgAlt: '#0E0E0E',
    sidebar: '#141414',
    accent: '#C9A962',
    accentGlow: 'rgba(201,169,98,0.4)',
    text: '#F5F5F0',
    textSecondary: '#C0C0B8',
    muted: '#A0A090',
    cardBg: '#1A1A1A',
    overlay: 'rgba(10,10,10,0.85)',
    label: 'Pro',
  },
  pro_plus: { 
    bg: '#0D0D0D',
    bgAlt: '#050505',
    sidebar: '#0A0A0A',
    accent: '#E8D5A3',
    accentGlow: 'rgba(232,213,163,0.35)',
    text: '#F5F5F0',
    textSecondary: '#D0D0C8',
    muted: '#B0B0A0',
    cardBg: '#141414',
    overlay: 'rgba(5,5,5,0.9)',
    label: 'Pro+',
  },
};

// =============================================================================
// TIERS - Subscription tier definitions
// =============================================================================

export const TIERS = {
  free: {
    rank: 0,
    light: true,
    name: 'Free',
    // Tier headers lead with WHO IT IS FOR, not what it costs.
    description: 'See exactly what we teach — then try it.',
    features: [
      // Each line answers "so what?". "Viewable catalog" is a function;
      // being able to inspect the whole thing before paying is the benefit.
      'Browse the entire catalogue before you pay a penny',
      'Selected courses free in full — lessons, homework and answer keys',
      'Progress saved across every device',
    ],
    // Rendered as dashes, not ticks. Naming what is missing is what makes the
    // upgrade legible; a free tier with no stated limits reads as the whole product.
    limitations: [
      'Most courses stay locked',
      'No AI search',
    ],
  },
  pro: {
    rank: 1,
    name: 'Pro',
    description: 'Every course we have built, unlocked.',
    features: [
      // Named courses, because a parent scanning for "the one my child is doing"
      // needs to find it in under a second. Vague scope ("full access") is
      // reassuring to nobody who is about to pay.
      'Pre-algebra, Algebra I, Algebra II and Geometry — complete',
      'Every lesson with its homework set and worked answer-key video',
      'Printable answer keys for checking at a glance',
      // The AI search's benefit is not "search" — it is arriving at the lesson
      // that covers the exact thing a student is stuck on.
      'Describe what you are stuck on and land on the lesson that covers it',
      // ⚠️ ROADMAP, NOT A CURRENT FEATURE. Worded as "as they ship" so nobody
      // subscribes tonight expecting Calculus tonight. If these slip, this line
      // becomes a broken promise on the page where you take money — delete it
      // rather than let it go stale. Swap for the honest alternative:
      //   'Pre-calculus and Calculus are in production'
      'Pre-calculus and Calculus included free as they ship',
    ],
    badge: 'Most families choose this',
  },
  pro_plus: {
    rank: 2,
    name: 'Pro+',
    // DRAFT COPY — Pro+ is not currently offered. The plan grid hides it (see
    // HIDDEN_TIERS in ManageSubscription.jsx); this tier stays defined here
    // because tierRank/isLocked must keep seeing it. Verify every line below is
    // something you will actually honour before it is shown to anyone.
    description: 'For a household, or a student who wants more room.',
    features: [
      'Everything in Pro',
      'Up to five learner profiles',
      'Early access to new subjects as they ship',
      'Priority support from the people who made it',
    ],
    badge: 'Premium',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export const getColorsForTier = (tier) => {
  return TIER_COLORS[tier] || TIER_COLORS.free;
};

export const isLightTheme = (tier) => {
  return !!(TIERS[tier] && TIERS[tier].light);
};

// ---------------------------------------------------------------------------
// TIER GATING — all access decisions compare rank, never the tier key itself.
// `tierRequired` is a fact about content; `locked` is always COMPUTED from it
// against the user's current tier. To rename a tier, change `name`; to reorder
// or insert one, change `rank`. Never write `tier === 'pro'` anywhere in the
// app — route every tier decision through these helpers.
// ---------------------------------------------------------------------------
export const tierRank = (tier) => (TIERS[tier] ? TIERS[tier].rank : 0);


// True when the user's tier is below the tier the content requires.
export const isLocked = (tierRequired, currentTier) =>
  tierRank(currentTier) < tierRank(tierRequired);

// GATED PAGES — the minimum plan for each. Both the sidebar (show/hide the tab)
// and the route (page vs. upgrade prompt) read this, through canUseFeature, which
// compares tier RANK like every other gate. To open a page to Pro later, change
// its 'pro_plus' to 'pro'. Nothing else needs touching.
export const FEATURE_MIN_TIER = {
  playlists: 'pro_plus',
  tools: 'pro_plus',
};
// Fails CLOSED: a feature name that isn't listed above (a typo, say) grants access
// to nobody, rather than to everyone.
export const canUseFeature = (feature, currentTier) =>
  Object.prototype.hasOwnProperty.call(FEATURE_MIN_TIER, feature) &&
  !isLocked(FEATURE_MIN_TIER[feature], currentTier);

//videoUrl: 'https://devstreaming-cdn.apple.com/videos/streaming/examples/adv_dv_atmos/main.m3u8'